import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import {
  FormControl,
  Input,
  FormField,
  FormMessage,
  Switch,
  FormLabel,
  Text,
} from '@/components'
import { When } from 'react-if'
import { yupResolver } from '@hookform/resolvers/yup'
import { cn } from '@/lib/utils'
import { CrawlerFormProps, CrawlerFormRef } from './CrawlerForm.types'
import {
  BooleanOptions,
  CrawlerFormFields,
  crawlerFormSchema,
  CrawlerAdvancedFields,
} from './CrawlerForm.schema'
import { FormSwitcGroup } from './CrawlerForm.utils'
import { useTranslations } from 'next-intl'
import { appSelectors, useAppStore } from '@/store'

export const CrawlerForm = forwardRef<CrawlerFormRef, CrawlerFormProps>(
  ({ onSubmit, onError }, ref) => {
    const form = useForm({
      resolver: yupResolver(crawlerFormSchema),
      defaultValues: {
        url: '',
      },
    })

    const t = useTranslations('crawler')
    const { setIsAdvancedEnabled } = useAppStore(appSelectors.actions)

    const { handleSubmit, control } = form

    const submit = useCallback(async () => {
      await handleSubmit(onSubmit, onError)()
    }, [handleSubmit, onError, onSubmit])

    const { advanced } = useWatch({
      control,
    })

    useImperativeHandle(
      ref,
      () => ({
        submit,
      }),
      [submit],
    )

    useEffect(() => {
      setIsAdvancedEnabled(!!advanced)
    }, [advanced, setIsAdvancedEnabled])

    return (
      <FormProvider {...form}>
        <FormField
          control={control}
          name={CrawlerFormFields.url}
          render={({ field }) => (
            <>
              <div className={'crawlerWrapper'}>
                <Input
                  placeholder={t('form.fields.url')}
                  {...field}
                  inputMode="url"
                  className="border-none p-3 lg:p-8 h-16 lg:h-20 text-base lg:text-2xl"
                />
                <button className="crawlerWrapper_button" onClick={submit}>
                  Crawl!
                </button>
              </div>
              <FormMessage className={'fieldError'} id={field.name} />
            </>
          )}
        />
        <div className="w-auto h-12 bg-primary flex max-md:flex-col max-md:h-auto text-center gap-2 justify-center items-center py-2 px-3 my-4 gradient-border-sm">
          <p className="text-white">{t('promo.title')}</p>
          <a href="#" className="gradient-text font-bold">
            {t('promo.link')}
          </a>
        </div>
        <div>
          <Text
            variant="heading2Xl"
            className="gradient-text text-4xl font-bold w-full text-center mt-12 lg:mt-24 xl:mt-32"
          >
            {t('banner.title')}
          </Text>
          <Text
            Element="p"
            variant="paragraphHuge"
            className="w-full font-bold text-center text-white p-3"
          >
            {t('banner.desc')}
          </Text>
        </div>
        <FormField
          control={control}
          name={CrawlerFormFields.advanced}
          render={({ field }) => (
            <>
              <FormControl className="w-full px-2 mt-8">
                <FormLabel
                  htmlFor={field.name}
                  className="text-white font-bold text-md gap-4"
                >
                  <Switch
                    {...field}
                    value={field.value ? BooleanOptions.yes : BooleanOptions.no}
                    defaultChecked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  {t('form.group.title')}
                </FormLabel>
              </FormControl>

              <FormMessage className={'fieldError'} id={field.name} />

              <p className="text-white pl-14 lg:pr-32 text-md leading-6 mb-2">
                Scan an entire domain and collect all internal page links (e.g.,
                example.com) <br />
                <strong>ATTENTION: </strong>Full domain crawling is not
                available for large platforms like eBay, Amazon, alibaba,
                Wikipedia, etc.
              </p>
            </>
          )}
        />
        <When condition={advanced}>
          <div
            className={cn(
              'w-full px-6 lg:px-14 py-3 text-white flex flex-col gap-3',
              'advancedWrapper',
            )}
          >
            <FormSwitcGroup
              arr={Object.values(CrawlerAdvancedFields)}
              control={control}
              isAdvanced={true}
            />
          </div>
        </When>
        <FormSwitcGroup
          arr={[CrawlerFormFields.onePage, CrawlerFormFields.specificRoute]}
          control={control}
          isAdvanced={false}
          customClass="py-3"
        />
      </FormProvider>
    )
  },
)

CrawlerForm.displayName = 'CrawlerForm'
